/**
 * ExploreAiPanel — Hybrid AI Assistant panel rendered at layout level.
 * Desktop (>1200px): Renders as an in-flow flex sibling of Wrapper.
 * Mobile (≤1200px): Renders as an OffCanvas overlay.
 *
 * @userstory US-EXP-13 Open AI Assistant from Shortlist
 * @userstory US-EXP-14 AI Assistant Conversation
 * @userstory US-EXP-20 AI Assistant — Split-View Panel
 * @userstory US-EXP-21 AI Assistant — Fixed Profile Cards
 * @userstory US-EXP-23 Persistent AI Sessions
 * @userstory US-EXP-24 Copy Message to Clipboard
 * @userstory US-EXP-25 Regenerate Response
 * @userstory US-EXP-26 Edit Message & Resubmit
 */
import React, { useEffect, useRef, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import OffCanvas, {
	OffCanvasHeader,
	OffCanvasTitle,
	OffCanvasBody,
} from '../../../components/bootstrap/OffCanvas';
import Icon from '../../../components/icon/Icon';
import Spinner from '../../../components/bootstrap/Spinner';
import Textarea from '../../../components/bootstrap/forms/Textarea';

import useDeviceScreen from '../../../hooks/useDeviceScreen';
import { useExploreAi } from '../../../contexts/exploreAiContext';

import './_exploreAiPanel.scss';

// =============================================================================
// Helpers
// =============================================================================

const getAvatarColorIndex = (name: string): number => {
	let hash = 0;
	for (let i = 0; i < name.length; i++) {
		hash = name.charCodeAt(i) + ((hash << 5) - hash);
	}
	return Math.abs(hash) % 10;
};

const getInitials = (name: string): string => {
	const parts = name.trim().split(/\s+/);
	if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
	return (parts[0]?.[0] || '?').toUpperCase();
};

// =============================================================================
// Constants
// =============================================================================

const AI_PANEL_MIN_WIDTH = 350;
const AI_PANEL_MAX_RATIO = 0.6;

// =============================================================================
// Component
// =============================================================================

/** @userstory US-EXP-20 */
const ExploreAiPanel: React.FC = () => {
	const { t } = useTranslation(['explore', 'common']);
	const navigate = useNavigate();
	const deviceScreen = useDeviceScreen();
	const isDesktop = (deviceScreen?.width ?? 0) > 1200;

	const {
		panelOpen,
		closePanel,
		resetSession,
		session,
		messages,
		input,
		setInput,
		streaming,
		sendMessage,
		stopStreaming,
		talentCards,
		removeTalent,
		panelWidth,
		setPanelWidth,
		panelExpanded,
		togglePanelWidth,
		sessionList,
		loadSessions,
		resumeSession,
		deleteSession,
		showSessionList,
		setShowSessionList,
		regenerateLastResponse,
		editAndResubmit,
	} = useExploreAi();

	const chatEndRef = useRef<HTMLDivElement | null>(null);
	const isResizingRef = useRef(false);
	const [isResizing, setIsResizing] = useState(false);

	// Local state for inline editing (US-EXP-26)
	const [editingIdx, setEditingIdx] = useState<number | null>(null);
	const [editingContent, setEditingContent] = useState('');
	// Copied message index for feedback (US-EXP-24)
	const [copiedIdx, setCopiedIdx] = useState<number | null>(null);

	// Auto-scroll chat to bottom
	useEffect(() => {
		chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
	}, [messages]);

	// Load sessions when panel opens with no active session (US-EXP-23)
	useEffect(() => {
		if (panelOpen && showSessionList) {
			loadSessions();
		}
	}, [panelOpen, showSessionList, loadSessions]);

	/** @userstory US-EXP-24 */
	const handleCopy = useCallback(
		async (content: string, idx: number) => {
			try {
				await navigator.clipboard.writeText(content);
				setCopiedIdx(idx);
				setTimeout(() => setCopiedIdx(null), 2000);
			} catch {
				// silently fail
			}
		},
		[],
	);

	// =========================================================================
	// Resize handler (US-EXP-20)
	// =========================================================================

	/** @userstory US-EXP-20 */
	const handleResizeMouseDown = useCallback(
		(e: React.MouseEvent) => {
			e.preventDefault();
			isResizingRef.current = true;
			setIsResizing(true);
			const startX = e.clientX;
			const startWidth = panelWidth;

			const onMouseMove = (ev: MouseEvent) => {
				if (!isResizingRef.current) return;
				const delta = startX - ev.clientX;
				const maxWidth = Math.round(window.innerWidth * AI_PANEL_MAX_RATIO);
				const newWidth = Math.min(
					maxWidth,
					Math.max(AI_PANEL_MIN_WIDTH, startWidth + delta),
				);
				setPanelWidth(newWidth);
			};

			const onMouseUp = () => {
				isResizingRef.current = false;
				setIsResizing(false);
				document.removeEventListener('mousemove', onMouseMove);
				document.removeEventListener('mouseup', onMouseUp);
				document.body.style.cursor = '';
				document.body.style.userSelect = '';
			};

			document.body.style.cursor = 'col-resize';
			document.body.style.userSelect = 'none';
			document.addEventListener('mousemove', onMouseMove);
			document.addEventListener('mouseup', onMouseUp);
		},
		[panelWidth, setPanelWidth],
	);

	// Don't render anything if panel is not open
	if (!panelOpen) return null;

	// =========================================================================
	// Shared content fragments (avoid duplication between desktop / mobile)
	// =========================================================================

	const resizeHandle = (
		<div
			className='explore-ai-resize-handle'
			onMouseDown={handleResizeMouseDown}
			role='separator'
			aria-orientation='vertical'
			aria-label='Resize panel'
		/>
	);

	const headerButtons = (
		<>
			{/* Toggle expand/collapse (US-EXP-20) */}
			<button
				type='button'
				className='explore-ai-toggle-btn'
				onClick={togglePanelWidth}
				aria-label={panelExpanded ? 'Collapse panel' : 'Expand panel'}>
				<Icon icon={panelExpanded ? 'ChevronRight' : 'ChevronLeft'} />
			</button>
			{/* Session history button (US-EXP-23) */}
			<button
				type='button'
				className={classNames('explore-ai-toggle-btn ms-1', { active: showSessionList })}
				onClick={() => {
					if (showSessionList) {
						setShowSessionList(false);
					} else {
						loadSessions();
					}
				}}
				aria-label={t('explore:ai.sessionHistory')}
				title={t('explore:ai.sessionHistory')}>
				<Icon icon='History' />
			</button>
			{/* New conversation button */}
			{session && (
				<button
					type='button'
					className='explore-ai-toggle-btn ms-1'
					onClick={resetSession}
					aria-label={t('explore:ai.newConversation')}
					title={t('explore:ai.newConversation')}>
					<Icon icon='Add' />
				</button>
			)}
		</>
	);

	/* Mini profile cards strip (US-EXP-21) */
	const profileStrip = session && talentCards.length > 0 && (
		<div className='explore-ai-profile-strip'>
			{talentCards.map((tc) => {
				const initials = getInitials(tc.fullName);
				const colorIdx = getAvatarColorIndex(tc.fullName);
				const shortName =
					tc.fullName.split(/\s+/).length >= 2
						? `${tc.fullName.split(/\s+/)[0]} ${tc.fullName.split(/\s+/).pop()?.[0]}.`
						: tc.fullName;
				return (
					<div key={tc.talentUserId} className='explore-ai-mini-card'>
						<span
							className='explore-ai-mini-card-link'
							role='link'
							tabIndex={0}
							onClick={() => navigate(`/talents/${tc.talentUserId}`)}
							onKeyDown={(e) =>
								e.key === 'Enter' && navigate(`/talents/${tc.talentUserId}`)
							}
							title={tc.fullName}>
							<div
								className={classNames(
									'explore-ai-mini-avatar',
									`explore-avatar--color-${colorIdx}`,
								)}>
								{tc.photoUrl ? (
									<img src={tc.photoUrl} alt={tc.fullName} />
								) : (
									initials
								)}
							</div>
							<span className='explore-ai-mini-name'>{shortName}</span>
						</span>
						<button
							type='button'
							className='explore-ai-mini-remove'
							onClick={() => removeTalent(tc.talentUserId)}
							aria-label={t('explore:ai.removeFromChat')}
							title={t('explore:ai.removeFromChat')}>
							×
						</button>
					</div>
				);
			})}
		</div>
	);

	/** Session list view (US-EXP-23) */
	const sessionListView = showSessionList && (
		<div className='explore-ai-session-list p-3'>
			<div className='d-flex align-items-center mb-3'>
				<h6 className='fw-bold mb-0 flex-grow-1'>{t('explore:ai.sessionHistory')}</h6>
				{session && (
					<button
						type='button'
						className='btn btn-sm btn-outline-primary'
						onClick={() => setShowSessionList(false)}>
						<Icon icon='ArrowBack' size='sm' className='me-1' />
						{t('explore:ai.backToChat')}
					</button>
				)}
			</div>
			{sessionList.length === 0 ? (
				<p className='text-muted'>{t('explore:ai.noSessions')}</p>
			) : (
				sessionList.map((s) => (
					<div
						key={s.sessionId}
						className={classNames(
							'explore-ai-session-item d-flex align-items-start gap-2 mb-2 p-3 rounded',
							{ 'explore-ai-session-item--active': session?.sessionId === s.sessionId },
						)}>
						<button
							type='button'
							className='flex-grow-1 text-start border-0 bg-transparent p-0 overflow-hidden'
							onClick={() => resumeSession(s.sessionId)}>
							<div className='fw-semibold text-truncate mb-1'>
								{s.title || t('explore:ai.untitledSession')}
							</div>
							<small className='text-muted d-block text-truncate'>
								{new Date(s.lastInteractionAt).toLocaleDateString()} ·{' '}
								{s.messageCount} {t('explore:ai.messagesCount')} ·{' '}
								{t('explore:ai.profileCount', { count: s.profileCount })}
							</small>
						</button>
						<button
							type='button'
							className='explore-ai-session-delete flex-shrink-0'
							onClick={() => deleteSession(s.sessionId)}
							aria-label={t('explore:ai.deleteSession')}
							title={t('explore:ai.deleteSession')}>
							<Icon icon='Delete' size='sm' />
						</button>
					</div>
				))
			)}
		</div>
	);

	/** Welcome view — shown when panel is open but no session active */
	const welcomeView = !session && (
		<div className='d-flex flex-column align-items-center justify-content-center h-100 px-4'>
			<div className='explore-ai-empty mt-4'>
				<div className='explore-ai-empty-icon'>
					<Icon icon='SmartToy' size='2x' />
				</div>
				<p className='text-muted mb-0'>{t('explore:ai.selectTalentsHint')}</p>
			</div>
		</div>
	);

	/** Chat message list + input (US-EXP-14, US-EXP-24, US-EXP-25, US-EXP-26) */
	const chatBody = session && (
		<div className='d-flex flex-column h-100'>
			{/* Chat Messages */}
			<div
				className='flex-grow-1 overflow-auto mb-3'
				role='log'
				aria-label={t('explore:ai.conversationLog')}>
				{messages.length === 0 && (
					<div className='explore-ai-empty mt-4'>
						<div className='explore-ai-empty-icon'>
							<Icon icon='Chat' size='2x' />
						</div>
						<p className='text-muted mb-3'>{t('explore:ai.conversationHint')}</p>
						{/* Suggested prompts — pick 3 random from 10 */}
						<div className='d-flex flex-column gap-2 mt-3'>
							{Array.from({ length: 10 }, (_, i) =>
								t(`explore:ai.prompt${i + 1}`),
							).map((prompt) => (
								<button
									type='button'
									key={prompt}
									className='explore-ai-prompt-chip'
									onClick={() => {
										setInput(prompt);
									}}>
									{prompt}
								</button>
							))}
						</div>
					</div>
				)}
				{messages.map((msg, idx) => {
					const isLastAssistant =
						msg.role === 'assistant' &&
						idx === messages.length - 1 &&
						!streaming;
					const isEditing = editingIdx === idx;

					return (
						<div
							key={msg.id || idx}
							className={classNames(
								'explore-ai-message mb-3 p-3 rounded position-relative',
								{
									'explore-ai-message--user ms-4': msg.role === 'user',
									'explore-ai-message--error me-4': msg.role === 'error',
									'explore-ai-message--assistant me-4':
										msg.role === 'assistant',
								},
							)}>
							<small className='d-block mb-1 fw-semibold opacity-75'>
								{msg.role === 'user'
									? t('explore:ai.you')
									: msg.role === 'error'
										? t('explore:ai.error')
										: t('explore:ai.assistant')}
							</small>

							{/* Inline edit mode (US-EXP-26) */}
							{isEditing && msg.role === 'user' ? (
								<div>
									<Textarea
										value={editingContent}
										onChange={(
											e: React.ChangeEvent<HTMLTextAreaElement>,
										) => setEditingContent(e.target.value)}
										rows={3}
										className='explore-ai-textarea mb-2'
									/>
									<div className='d-flex gap-2'>
										<button
											type='button'
											className='btn btn-sm btn-primary'
											onClick={() => {
												const msgIndex =
													msg.messageIndex ?? idx;
												setEditingIdx(null);
												editAndResubmit(
													msgIndex,
													editingContent,
												);
											}}>
											{t('common:Send')}
										</button>
										<button
											type='button'
											className='btn btn-sm btn-outline-secondary'
											onClick={() => setEditingIdx(null)}>
											{t('common:Cancel')}
										</button>
									</div>
								</div>
							) : msg.role === 'assistant' ? (
								<div className='explore-ai-markdown'>
									<ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
								</div>
							) : (
								<div style={{ whiteSpace: 'pre-wrap' }}>{msg.content}</div>
							)}

							{/* Message action buttons (hover) */}
							{!isEditing && !streaming && msg.role !== 'error' && (
								<div className='explore-ai-msg-actions'>
									{/* Copy (US-EXP-24) */}
									<button
										type='button'
										className='explore-ai-msg-action-btn'
										onClick={() => handleCopy(msg.content, idx)}
										title={t('explore:ai.copyMessage')}
										aria-label={t('explore:ai.copyMessage')}>
										<Icon
											icon={
												copiedIdx === idx
													? 'Check'
													: 'ContentCopy'
											}
											size='sm'
										/>
									</button>
									{/* Edit (US-EXP-26) — user messages only */}
									{msg.role === 'user' && (
										<button
											type='button'
											className='explore-ai-msg-action-btn'
											onClick={() => {
												setEditingIdx(idx);
												setEditingContent(msg.content);
											}}
											title={t('explore:ai.editMessage')}
											aria-label={t('explore:ai.editMessage')}>
											<Icon icon='Edit' size='sm' />
										</button>
									)}
									{/* Regenerate (US-EXP-25) — last assistant message only */}
									{isLastAssistant && (
										<button
											type='button'
											className='explore-ai-msg-action-btn'
											onClick={regenerateLastResponse}
											title={t('explore:ai.regenerate')}
											aria-label={t('explore:ai.regenerate')}>
											<Icon icon='Refresh' size='sm' />
										</button>
									)}
								</div>
							)}
						</div>
					);
				})}
				{streaming && (
					<div className='text-center'>
						<Spinner size='sm' />
					</div>
				)}
				<div ref={chatEndRef} />
			</div>

			{/* Message Input (US-EXP-14) — Copilot-style textarea */}
			<div className='explore-ai-input-box'>
				<Textarea
					value={input}
					onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
						setInput(e.target.value)
					}
					placeholder={t('explore:ai.inputPlaceholder')}
					onKeyDown={(e: React.KeyboardEvent) => {
						if (e.key === 'Enter' && !e.shiftKey) {
							e.preventDefault();
							sendMessage();
						}
					}}
					disabled={streaming}
					aria-label={t('explore:ai.inputPlaceholder')}
					rows={3}
					className='explore-ai-textarea'
				/>
				<button
					type='button'
					className={classNames('explore-ai-send-btn', {
						'explore-ai-send-btn--stop': streaming,
					})}
					onClick={streaming ? stopStreaming : sendMessage}
					disabled={!streaming && !input.trim()}
					aria-label={streaming ? t('explore:ai.stop') : t('common:Send')}>
					{streaming
						? <span style={{ width: 10, height: 10, borderRadius: 2, background: '#fff', display: 'block' }} />
						: <Icon icon='ArrowUpward' />}
				</button>
			</div>
		</div>
	);

	// =========================================================================
	// Desktop: In-flow panel (flex sibling of Wrapper — US-EXP-20)
	// =========================================================================
	if (isDesktop) {
		return (
			<div
				className={classNames('explore-ai-panel-desktop', { 'is-resizing': isResizing })}
				style={{ width: `${panelWidth}px` }}
				aria-label={t('explore:ai.title')}>
				{resizeHandle}
				<div className='explore-ai-panel-header d-flex align-items-center'>
					<Icon icon='SmartToy' className='me-2' />
					<span className='fw-bold'>{t('explore:ai.title')}</span>
					<span className='flex-grow-1' />
					{headerButtons}
					<button
						type='button'
						className='btn-close btn-close-white ms-2'
						onClick={() => closePanel()}
						aria-label='Close'
					/>
				</div>
				{profileStrip}
				<div className='explore-ai-panel-body'>
					{showSessionList ? sessionListView : (chatBody || welcomeView)}
				</div>
			</div>
		);
	}

	// =========================================================================
	// Mobile: OffCanvas overlay (no isRightPanel — pure overlay, via Portal)
	// =========================================================================
	return (
		<OffCanvas
			isOpen={panelOpen}
			setOpen={(open: boolean) => {
				if (!open) closePanel();
			}}
			style={{ width: `${panelWidth}px` }}
			aria-label={t('explore:ai.title')}>
			{resizeHandle}
			<OffCanvasHeader setOpen={() => closePanel()} className='explore-ai-panel-header'>
				<OffCanvasTitle id='exploreAiPanel'>
					<Icon icon='SmartToy' className='me-2' />
					{t('explore:ai.title')}
				</OffCanvasTitle>
				{headerButtons}
			</OffCanvasHeader>
			{profileStrip}
			<OffCanvasBody>
				{showSessionList ? sessionListView : (chatBody || welcomeView)}
			</OffCanvasBody>
		</OffCanvas>
	);
};

export default ExploreAiPanel;
